// Sheet assembly, validation and the JSON-serialisable export (DESIGN §9 step 7).
// One pass over the authored animation frames produces, from the SAME solved
// skeleton per frame: the body cell, the glow cell and the MechFrameMeta.

import { CELL, FOOT_ANCHOR } from '../../config/constants';
import { ANIM_FRAMES, ANIM_KEYS, ATTACH_KEYS, type AnimKey, type MechFrameMeta, type MechId } from '../../core/types';
import { canvas, ctx2d } from './draw';
import { CellPainter } from './painter';
import { ANIM_TIMING, buildAnimFrames, type FrameSpec } from './poses';
import { attachPoints, solve, SOLE_LINE, type Skeleton } from './rig';
import type { MechStyle } from './styles';

export const SHEET_COLUMNS = 10;
export const FRAME_COUNT = ANIM_KEYS.reduce((n, k) => n + ANIM_FRAMES[k], 0);
export const SHEET_ROWS = Math.ceil(FRAME_COUNT / SHEET_COLUMNS);

/** First sheet frame of each animation (ANIM_KEYS order). */
export const ANIM_START: Readonly<Record<AnimKey, number>> = (() => {
  const out = {} as Record<AnimKey, number>;
  let i = 0;
  for (const k of ANIM_KEYS) {
    out[k] = i;
    i += ANIM_FRAMES[k];
  }
  return out;
})();

export const cellXY = (i: number): { x: number; y: number } => ({
  x: (i % SHEET_COLUMNS) * CELL,
  y: Math.floor(i / SHEET_COLUMNS) * CELL,
});

export interface SolvedFrame {
  anim: AnimKey;
  index: number;
  spec: FrameSpec;
  sk: Skeleton;
  meta: MechFrameMeta;
}

export interface BuiltSheets {
  frames: SolvedFrame[];
  body: HTMLCanvasElement;
  glow: HTMLCanvasElement;
  warnings: string[];
}

const r3 = (v: number): number => Math.round(v * 1000) / 1000;

function metaOf(style: MechStyle, spec: FrameSpec, sk: Skeleton): MechFrameMeta {
  const meta: MechFrameMeta = {
    attach: attachPoints(style, sk),
    armF: { upper: r3(spec.armF.upper), fore: r3(spec.armF.fore) },
    armR: { upper: r3(spec.armR.upper), fore: r3(spec.armR.fore) },
    aimWeight: r3(spec.aimWeight),
  };
  if (spec.recoil !== undefined) meta.recoil = spec.recoil;
  if (spec.lift !== undefined) meta.lift = spec.lift;
  return meta;
}

/** Solve every frame (skeleton + metadata) without painting. */
export function solveFrames(style: MechStyle): SolvedFrame[] {
  const anims = buildAnimFrames(style);
  const out: SolvedFrame[] = [];
  for (const anim of ANIM_KEYS) {
    anims[anim].forEach((spec, index) => {
      const sk = solve(style, spec.pose);
      out.push({ anim, index, spec, sk, meta: metaOf(style, spec, sk) });
    });
  }
  return out;
}

/** Checks a frame: grounded feet on the ground line, legs reach their targets, nothing clipped by the cell. */
function validate(style: MechStyle, f: SolvedFrame, body: CanvasRenderingContext2D, x: number, y: number, out: string[]): void {
  const tag = `${style.id}/${f.anim}#${f.index}`;
  if (!f.sk.legF.reached || !f.sk.legR.reached) out.push(`${tag}: leg target out of reach`);
  if (f.spec.grounded) {
    // The lowest sole edge (the planted foot) must sit on the ground line.
    const planted = Math.max(f.sk.legF.soleY, f.sk.legR.soleY);
    if (Math.abs(planted - SOLE_LINE) > 0.35) out.push(`${tag}: planted sole at ${planted.toFixed(2)} (expected ${SOLE_LINE.toFixed(2)})`);
  }
  const img = body.getImageData(x, y, CELL, CELL).data;
  for (let i = 0; i < CELL; i++) {
    const edges = [i * 4 + 3, ((CELL - 1) * CELL + i) * 4 + 3, i * CELL * 4 + 3, (i * CELL + CELL - 1) * 4 + 3];
    if (edges.some((e) => img[e] > 10)) {
      out.push(`${tag}: touches the cell border`);
      return;
    }
  }
}

/** Paint the body and glow sheets for one mech. */
export function buildSheets(style: MechStyle): BuiltSheets {
  const frames = solveFrames(style);
  const W = SHEET_COLUMNS * CELL;
  const H = SHEET_ROWS * CELL;
  const body = canvas(W, H);
  const glow = canvas(W, H);
  const bg = ctx2d(body);
  const gg = ctx2d(glow);
  const painter = new CellPainter(CELL, 1);
  const warnings: string[] = [];
  frames.forEach((f, i) => {
    const { x, y } = cellXY(i);
    painter.render(style, f.sk, f.spec.fx, bg, gg, x, y);
    validate(style, f, bg, x, y, warnings);
  });
  return { frames, body, glow, warnings };
}

export interface SheetExport {
  version: 1;
  mechId: MechId;
  cell: number;
  footAnchor: { x: number; y: number };
  columns: number;
  rows: number;
  frameCount: number;
  frameOrder: AnimKey[];
  attachKeys: string[];
  textures: Record<string, string>;
  arm: { upperLen: number; foreLen: number; upperOrigin: { x: number; y: number }; foreOrigin: { x: number; y: number }; rearTint: number };
  anims: Record<AnimKey, { phaserKey: string; start: number; count: number; fps: number; loop: boolean }>;
  /** Per frame, in sheet order. */
  frames: (MechFrameMeta & { anim: AnimKey; index: number; sheetFrame: number; cellX: number; cellY: number; grounded: boolean })[];
}

export function exportMeta(
  style: MechStyle,
  frames: SolvedFrame[],
  textures: Record<string, string>,
  arm: SheetExport['arm'],
  animKey: (anim: AnimKey) => string,
): SheetExport {
  const anims = {} as SheetExport['anims'];
  for (const k of ANIM_KEYS) {
    anims[k] = { phaserKey: animKey(k), start: ANIM_START[k], count: ANIM_FRAMES[k], ...ANIM_TIMING[k] };
  }
  return {
    version: 1,
    mechId: style.id,
    cell: CELL,
    footAnchor: { x: FOOT_ANCHOR.x, y: FOOT_ANCHOR.y },
    columns: SHEET_COLUMNS,
    rows: SHEET_ROWS,
    frameCount: FRAME_COUNT,
    frameOrder: [...ANIM_KEYS],
    attachKeys: [...ATTACH_KEYS],
    textures,
    arm,
    anims,
    frames: frames.map((f, i) => {
      const { x, y } = cellXY(i);
      return { ...f.meta, anim: f.anim, index: f.index, sheetFrame: i, cellX: x, cellY: y, grounded: f.spec.grounded };
    }),
  };
}
